import { useState, useCallback } from "react";
import { Lightbulb, Search, Send, Loader2, CheckCircle, Smartphone, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import PhoneSpecsPreview from "@/components/PhoneSpecsPreview";

interface PhoneResult {
  found: boolean;
  name: string;
  brand: string;
  image_url: string;
  description: string;
  specs?: {
    screen: string;
    processor: string;
    ram: string;
    storage: string;
    camera: string;
    frontCamera: string;
    battery: string;
    os: string;
    connectivity: string;
    weight: string;
  };
  ratings?: {
    screen: number;
    performance: number;
    camera: number;
    battery: number;
    value: number;
    overall: number;
  };
}

const PhoneSuggestions = () => {
  const { toast } = useToast();
  const [phoneName, setPhoneName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [phoneResult, setPhoneResult] = useState<PhoneResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showSpecs, setShowSpecs] = useState(false);

  const searchPhone = useCallback(async () => {
    if (!phoneName.trim() || phoneName.trim().length < 3) return;
    setSearching(true);
    setPhoneResult(null);
    setImageError(false);
    setShowSpecs(false);

    try {
      const { data, error } = await supabase.functions.invoke("search-phone-image", {
        body: { phone_name: phoneName.trim() },
      });

      if (error) throw error;
      if (data?.success && data?.data) {
        const result = data.data;
        if (!result.name) result.name = phoneName.trim();
        if (!result.description) result.description = 'Modelo buscado pelo nome fornecido.';
        setPhoneResult(result);
      }
    } catch {
      toast({ title: "Erro ao buscar celular", variant: "destructive" });
    } finally {
      setSearching(false);
    }
  }, [phoneName, toast]);

  const handleSubmit = async () => {
    if (!phoneName.trim()) {
      toast({ title: "Digite o nome do celular", variant: "destructive" });
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const finalPhoneName = phoneResult?.name || phoneName.trim();

    try {
      // Get current user id if logged in
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id ?? null;

      if (customerPhone.trim()) {
        const { data: existing } = await supabase
          .from("phone_suggestions")
          .select("id")
          .eq("customer_phone", customerPhone.trim())
          .eq("phone_name", finalPhoneName);

        if (existing && existing.length > 0) {
          toast({ title: "Você já sugeriu este modelo!", description: "Tente sugerir um modelo diferente.", variant: "destructive" });
          setSubmitting(false);
          return;
        }
      }

      const { error } = await supabase.from("phone_suggestions").insert({
        phone_name: finalPhoneName,
        phone_image_url: phoneResult?.image_url || null,
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
        user_id: currentUserId,
      });

      if (error) throw error;

      setSubmitted(true);
      toast({ title: "Sugestão enviada!", description: "Obrigado pela sua sugestão!" });
    } catch {
      toast({ title: "Erro ao enviar sugestão", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setPhoneName("");
    setCustomerName("");
    setCustomerPhone("");
    setPhoneResult(null);
    setSubmitted(false);
    setImageError(false);
    setShowSpecs(false);
  };

  if (submitted) {
    return (
      <section id="sugestoes" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="text-center py-12 space-y-4">
            <CheckCircle className="w-16 h-16 text-primary mx-auto" />
            <h3 className="font-display text-2xl font-bold text-foreground">Sugestão Enviada!</h3>
            <p className="text-muted-foreground">Vamos analisar e trazer novidades para você.</p>
            <button
              onClick={handleReset}
              className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-all hover:scale-105"
            >
              Enviar outra sugestão
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="sugestoes" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lightbulb className="w-8 h-8 text-primary" />
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gradient-gold">Sugira um Celular</h2>
          </div>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Qual celular você gostaria de ver na nossa loja? Digite o modelo e veja se é esse mesmo!
          </p>
        </div>

        <div className="max-w-lg mx-auto space-y-4">
          {/* Phone search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Ex: iPhone 16 Pro Max, Samsung Galaxy S25..."
                value={phoneName}
                onChange={(e) => setPhoneName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchPhone()}
                className="w-full pl-10 pr-3 py-3 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
                maxLength={100}
              />
            </div>
            <button
              onClick={searchPhone}
              disabled={searching || phoneName.trim().length < 3}
              className="px-4 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Buscar celular"
            >
              {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </button>
          </div>

          {/* Phone result */}
          {phoneResult && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="w-32 h-32 flex-shrink-0 bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
                  {phoneResult.found && phoneResult.image_url && !imageError ? (
                    <img
                      src={phoneResult.image_url}
                      alt={phoneResult.name}
                      className="w-full h-full object-contain"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <Smartphone className="w-12 h-12 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display text-lg font-bold text-foreground">{phoneResult.name}</h4>
                  {phoneResult.brand && phoneResult.brand !== "unknown" && (
                    <p className="text-sm text-primary font-semibold">{phoneResult.brand}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">{phoneResult.description}</p>
                  {phoneResult.ratings?.overall && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs font-semibold text-muted-foreground">Nota geral:</span>
                      <span className={`text-sm font-bold ${phoneResult.ratings.overall >= 8 ? 'text-green-500' : phoneResult.ratings.overall >= 6 ? 'text-primary' : 'text-destructive'}`}>
                        {phoneResult.ratings.overall.toFixed(1)}/10
                      </span>
                    </div>
                  )}
                  {!phoneResult.found && (
                    <p className="text-xs text-destructive mt-2">Modelo não identificado, mas você ainda pode sugerir!</p>
                  )}
                </div>
              </div>

              {/* Specs & Ratings toggle */}
              {phoneResult.found && (phoneResult.specs || phoneResult.ratings) && (
                <>
                  <button
                    onClick={() => setShowSpecs(!showSpecs)}
                    className="w-full flex items-center justify-center gap-1 text-sm text-primary font-semibold hover:opacity-80 transition-opacity py-1"
                  >
                    {showSpecs ? "Ocultar detalhes" : "Ver especificações e notas"}
                    {showSpecs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showSpecs && (
                    <PhoneSpecsPreview specs={phoneResult.specs} ratings={phoneResult.ratings} />
                  )}
                </>
              )}
            </div>
          )}

          {/* Customer info */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Seu nome (opcional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
              maxLength={100}
            />
            <input
              type="tel"
              placeholder="Seu WhatsApp (opcional)"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none transition-colors"
              maxLength={20}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !phoneName.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-gradient-gold text-primary-foreground font-semibold hover:opacity-90 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Enviar Sugestão
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PhoneSuggestions;
